import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { SERVICES } from "../../lib/constants";
import { ArrowRight, ShieldCheck, Zap, MapPin, Star } from "lucide-react";

const HERO_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_castle-ops-1/artifacts/q2sxd95b_12.png";

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative bg-[#08080A] text-white overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 75% 20%, rgba(0,122,255,0.18), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(0,229,255,0.08), transparent 50%)" }} />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-24 pb-20 lg:pt-32 lg:pb-28 relative z-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="overline-cc mb-6 fade-up flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--cc-accent)] pulse-dot" /> Command Interface Active
            </p>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight fade-up-1">
              Deploy your crew.<br />
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(90deg, #007AFF, #00E5FF)" }}>Execute with precision.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-white/60 max-w-xl leading-relaxed fade-up-2">
              Precision operations. Vetted operatives. Full regional coverage. CastleCorp runs moving, delivery, assembly, and junk removal like a mission — planned, tracked, and executed to the minute.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 fade-up-3">
              <Link to="/book">
                <Button data-testid="hero-book-button" size="lg" className="rounded-full h-12 px-8 btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold text-base">
                  Book Now — Instant Estimate <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/quote">
                <Button data-testid="hero-quote-button" size="lg" variant="outline" className="rounded-full h-12 px-8 border-white/25 text-white hover:bg-white/10 hover:text-white bg-transparent text-base">
                  Request Custom Quote
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block fade-up-2">
            <div className="rounded-2xl overflow-hidden border border-white/10 card-lift bg-[#050607]">
              <img src={HERO_IMG} alt="CastleCorp — insignia" className="w-full h-[420px] object-contain p-6" />
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 relative z-10">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-wrap items-center justify-center lg:justify-between gap-x-8 gap-y-2">
            {["SAME-DAY AVAILABLE", "SAN DIEGO & OCEANSIDE", "LONG DISTANCE TO LA & VEGAS", "INSURED UP TO $1M"].map((t) => (
              <span key={t} className="font-mono-cc text-[11px] tracking-[0.2em] text-white/40 flex items-center gap-3">
                <span className="w-1 h-1 rounded-full bg-[var(--cc-accent)]" />{t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <p className="overline-cc mb-3">Capabilities</p>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight max-w-lg">Seven divisions. One standard of execution.</h2>
          <Link to="/services" className="text-sm font-semibold text-[var(--cc-accent)] flex items-center gap-1 group" data-testid="home-all-services-link">
            All services <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => (
            <Link key={s.id} to="/quote" state={{ service: s.id }} data-testid={`home-service-card-${s.id}`}
              className={`card-lift rounded-xl border bg-card p-6 group ${i === 0 ? "lg:col-span-2 lg:row-span-2 flex flex-col justify-between" : ""}`}>
              <div>
                <s.icon className="w-6 h-6 text-[var(--cc-accent)]" />
                <h3 className={`font-heading font-semibold mt-4 ${i === 0 ? "text-2xl" : "text-lg"}`}>{s.name}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.blurb}</p>
              </div>
              <p className="mt-5 font-mono-cc text-xs text-muted-foreground">FROM <span className="text-foreground font-semibold">{s.from}</span></p>
            </Link>
          ))}
          <div className="rounded-xl border border-dashed p-6 flex flex-col justify-center items-start bg-secondary/40">
            <p className="overline-cc">CastleCorp X</p>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">An intelligent operating system powering the future of services.</p>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="border-y bg-secondary/40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-3 gap-10">
          {[
            { icon: ShieldCheck, title: "Vetted & Insured", text: "Every operative is background-checked and trained. Every job is covered up to $1M." },
            { icon: Zap, title: "One Price. No Variables.", text: "Your estimate is your price. No hourly creep, no surprise fees, no fine print." },
            { icon: MapPin, title: "Full Regional Coverage", text: "San Diego and Oceanside daily. Long-distance corridors to Los Angeles and Las Vegas." },
          ].map((f) => (
            <div key={f.title} className="flex gap-5">
              <div className="w-12 h-12 rounded-xl bg-[var(--cc-accent)]/10 flex items-center justify-center shrink-0">
                <f.icon className="w-5 h-5 text-[var(--cc-accent)]" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <p className="overline-cc mb-3">Protocol</p>
        <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight mb-14">Three steps to deployment.</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            ["01", "Consult", "Tell us the mission — what, where, when. Our AI assistant or team responds within the hour."],
            ["02", "Estimate", "Get one precise number — AI-refined from your photos. Locked in. No variables, ever."],
            ["03", "Book", "Confirm and track everything — crew, vehicle, timeline — from your portal."],
          ].map(([n, t, d]) => (
            <div key={n} className="relative rounded-xl border bg-card p-8 card-lift">
              <span className="font-mono-cc text-5xl font-semibold text-[var(--cc-accent)]/20">{n}</span>
              <h3 className="font-heading font-semibold text-xl mt-4">{t}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t bg-[#08080A] text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <p className="overline-cc mb-3">Field Reports</p>
          <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight mb-14">Trusted by people who value execution.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ["They ran my move like a flight operation. On time, wrapped everything, zero damage.", "K. Alvarez — La Jolla"],
              ["The flat rate was the flat rate. Crew was sharp, professional, fast. Never using hourly movers again.", "D. Thompson — Oceanside"],
              ["White-glove delivery for a $12k dining set. Placed, leveled, packaging gone in 40 minutes.", "S. Nguyen — Del Mar"],
            ].map(([quote, who]) => (
              <div key={who} className="rounded-xl border border-white/10 bg-white/[0.03] p-7">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[var(--cc-accent)] text-[var(--cc-accent)]" />)}</div>
                <p className="text-sm text-white/80 leading-relaxed">"{quote}"</p>
                <p className="mt-5 font-mono-cc text-[11px] tracking-widest text-white/40 uppercase">{who}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="rounded-2xl border p-10 lg:p-16 relative overflow-hidden bg-card">
          <div className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(ellipse at 90% 10%, var(--cc-glow), transparent 50%)" }} />
          <div className="relative z-10 max-w-2xl">
            <h2 className="font-heading font-bold text-3xl lg:text-5xl tracking-tight">Ready to deploy?</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">Get a precise estimate in minutes. Same-day availability across San Diego and Oceanside.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/quote">
                <Button data-testid="cta-quote-button" size="lg" className="rounded-full h-12 px-8 btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">
                  Request Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button data-testid="cta-contact-button" size="lg" variant="outline" className="rounded-full h-12 px-8">Talk to Us</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
